import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaBriefcase, FaProjectDiagram, FaTools, FaEnvelope, FaFileAlt } from 'react-icons/fa';

// Desktop/Tablet Navbar
const NavBarWrap = styled.nav`
  background: rgb(13,19,33);
  border-radius: 30px;
  max-width: 800px;
  margin: 24px auto 0 auto;
  box-shadow: 0 3px 16px rgba(56,73,114,0.12);
  padding: 0;

  @media (max-width: 600px) {
    display: none;
  }
`;

const Tabs = styled.ul`
  display: flex;
  justify-content: space-between;
  align-items: center;
  list-style: none;
  margin: 0;
  padding: 0 24px;
  height: 62px;
`;

const Tab = styled.a`
  flex: 1 0 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px 0 6px 0;
  border-radius: 20px;
  background: ${({ active }) =>
    active ? 'linear-gradient(90deg,#3470f7 60%,#1c274b 100%)' : 'transparent'};
  color: ${({ active }) => (active ? '#fff' : '#99c5ff')};
  font-weight: 600;
  font-size: 1.09em;
  margin: 0 7px;
  text-decoration: none;
  letter-spacing: 0.01em;
  min-width: 80px;
  transition: background 0.18s, color 0.18s;

  &:hover,
  &:focus {
    color: #fff;
    background: linear-gradient(90deg,#4894ee 45%,#174477 100%);
  }

  svg {
    font-size: 1.5em;
    margin-right: 10px;
    transition: font-size 0.18s;
  }
`;

// Mobile Floating Tab Bar
const MobileNav = styled.nav`
  display: none;
  position: fixed;
  bottom: 15px;
  left: 0;
  right: 0;
  justify-content: space-around;
  background: rgb(13,19,33);
  border-radius: 18px;
  margin: 0 12px;
  padding: 8px 0 5px 0;
  z-index: 2000;
  box-shadow: 0 2px 12px 0 rgba(56,73,114,0.21);
  transition: transform 0.3s ease-in-out;

  /* Hide when prop 'hidden' is true */
  transform: ${({ hidden }) => (hidden ? 'translateY(110%)' : 'translateY(0)')};

  @media (max-width: 600px) {
    display: flex;
  }
`;

const MobileTab = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: ${({ active }) => (active ? '#fff' : '#dbe8fa')};
  font-size: 0.9em;
  font-weight: 500;
  border-radius: 12px;
  padding: 5px 10px;
  min-width: 60px;

  &:active,
  &:focus,
  &:hover {
    color: #fff;
    background: #225dba;
  }

  svg {
    font-size: 1.8em;
    margin-bottom: 4px;
  }
`;

const NavLabel = styled.span`
  font-size: 0.9em;
  white-space: nowrap;  /* prevent wrapping */
`;

const Navbar = () => {
  const [active, setActive] = useState('about');
  const [hidden, setHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  const tabs = [
    { id: 'experience', label: 'Experience', icon: <FaBriefcase />, href: '#experience' },
    { id: 'projects', label: 'Projects', icon: <FaProjectDiagram />, href: '#projects' },
    { id: 'skills', label: 'Skills', icon: <FaTools />, href: '#skills' },
    { id: 'contact', label: 'Contact', icon: <FaEnvelope />, href: '#contact' },
    {
      id: 'resume',
      label: 'Resume',
      icon: <FaFileAlt />,
      href: `${process.env.PUBLIC_URL}/Amirtha_resume.pdf`,
      external: true,
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        // Scrolling down -> hide navbar
        setHidden(true);
      } else {
        // Scrolling up -> show navbar
        setHidden(false);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <>
      {/* Desktop/Laptop Navbar */}
      <NavBarWrap>
        <Tabs>
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              active={active === tab.id}
              href={tab.href}
              target={tab.external ? '_blank' : undefined}
              rel={tab.external ? 'noopener noreferrer' : undefined}
              onClick={() => setActive(tab.id)}
            >
              {tab.icon}
              <NavLabel>{tab.label}</NavLabel>
            </Tab>
          ))}
        </Tabs>
      </NavBarWrap>

      {/* Mobile Floating Tab Bar */}
      <MobileNav hidden={hidden}>
        {tabs.map((tab) => (
          <MobileTab
            key={tab.id}
            active={active === tab.id}
            href={tab.href}
            target={tab.external ? '_blank' : undefined}
            rel={tab.external ? 'noopener noreferrer' : undefined}
            onClick={() => setActive(tab.id)}
          >
            {tab.icon}
            <NavLabel>{tab.label}</NavLabel>
          </MobileTab>
        ))}
      </MobileNav>
    </>
  );
};

export default Navbar;
